import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFamilyTerminology } from '../hooks/useFamilyTerminology';
import SocialLoginButtons from './SocialLoginButtons';
import { isValidRedirectUrl, getSafeRedirectUrl } from '../utils/authUtils';
import { validatePassword, validateConfirmPassword, isRegistrationFormValid } from '../utils/passwordValidation';
import {
  Container,
  Paper,
  Box,
  TextField,
  Button,
  Typography,
  Link,
  Divider,
  Alert,
  CircularProgress,
  Avatar,
  Grid,
  IconButton,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { Login as LoginIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import { getPrivacyPolicy, getTermsOfService, acknowledgePolicyDocument, completeAlexaOAuthFlow, setCurrentAccessToken } from '../services/api';
import type { PolicyDocument } from '../types/api';
import { renderPolicyMarkdown } from '../utils/sanitize';
import Footer from './Footer';
import PolicyAcceptanceModal from './PolicyAcceptanceModal';

// Feature highlights for the login page intro
const FEATURE_HIGHLIGHTS = [
  { emoji: '⭐', text: 'Log Moments Instantly' },
  { emoji: '📊', text: 'See Patterns Clearly' },
  { emoji: '❤️', text: 'Build Consistency Together' }
] as const;

const Login: React.FC = () => {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { basePath } = useFamilyTerminology();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    password: false,
    confirmPassword: false,
  });
  const [touched, setTouched] = useState({
    password: false,
    confirmPassword: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Policy state
  const [policies, setPolicies] = useState<{
    privacy: PolicyDocument | null;
    terms: PolicyDocument | null;
  }>({
    privacy: null,
    terms: null
  });
  const [policyScrolled, setPolicyScrolled] = useState({
    privacy: false,
    terms: false
  });
  const [policyAccepted, setPolicyAccepted] = useState({
    privacy: false,
    terms: false
  });
  const [loadingPolicies, setLoadingPolicies] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);

  // Alexa OAuth state
  const [alexaOAuthParams, setAlexaOAuthParams] = useState<{
    authCode: string;
    clientId: string;
  } | null>(null);
  const [isCompletingAlexaOAuth, setIsCompletingAlexaOAuth] = useState(false);

  // Social provider availability state
  const [hasSocialProviders, setHasSocialProviders] = useState<boolean | null>(null);

  // Load policies for registration
  const loadPolicies = useCallback(async () => {
    if (policies.privacy && policies.terms) return; // Already loaded
    
    try {
      setLoadingPolicies(true);
      const [privacy, terms] = await Promise.all([
        getPrivacyPolicy(),
        getTermsOfService()
      ]);
      setPolicies({ privacy, terms });
    } catch (err) {
      console.error('Failed to load policies:', err);
      setError('Failed to load privacy policy and terms of service');
    } finally {
      setLoadingPolicies(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- policies intentionally omitted; adding them would cause an infinite loop since this callback sets policies state
  }, []);

  // Load policies when component mounts if in registration mode
  useEffect(() => {
    if (!isLogin) {
      loadPolicies();
    }
  }, [isLogin, loadPolicies]);

  // Function to complete Alexa OAuth flow
  const handleCompleteAlexaOAuth = useCallback(async (params: { authCode: string }) => {
    try {
      setIsCompletingAlexaOAuth(true);
      setError('');

      const response = await completeAlexaOAuthFlow(params);

      // Redirect to Alexa's callback URL
      window.location.replace(response.redirectUri);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to complete Alexa account linking');
      setIsCompletingAlexaOAuth(false);
    }
  }, []);

  // Handle OAuth callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const userId = urlParams.get('userId');
    const hasFamily = urlParams.get('hasFamily') === 'true';
    const error = urlParams.get('error');
    const returnUrl = urlParams.get('returnUrl');

    // Check for Alexa OAuth parameters in the callback URL
    const alexaLinking = urlParams.get('alexa_linking') === 'true';
    const alexaAuthCode = urlParams.get('auth_code');

    if (error) {
      const errorMessages: { [key: string]: string } = {
        oauth_failed: 'Social login failed. Please try again.',
        missing_profile_data: 'Unable to get profile information from social provider.',
        auth_failed: 'Authentication failed. Please try again.',
        server_error: 'Server error occurred. Please try again later.',
        account_locked: 'Sorry, there is an error with your account. Please contact support@letsreset.ai'
      };
      setError(errorMessages[error] || 'An unknown error occurred.');
    } else if (token && userId) {
      // Store authentication data — access token in module variable (not localStorage)
      setCurrentAccessToken(token);
      localStorage.setItem('userId', userId);
      localStorage.setItem('hasFamily', hasFamily.toString());

      // Check if this is an Alexa OAuth callback
      if (alexaLinking && alexaAuthCode) {
        // Store params and complete Alexa OAuth flow
        setAlexaOAuthParams({ authCode: alexaAuthCode, clientId: '' });

        // Small delay to ensure state is saved
        setTimeout(() => {
          handleCompleteAlexaOAuth({ authCode: alexaAuthCode });
        }, 100);
        return; // Don't redirect, let Alexa OAuth completion handle it
      }

      // Regular OAuth flow - redirect to return URL or dashboard
      let redirectUrl = '/';
      if (returnUrl && isValidRedirectUrl(decodeURIComponent(returnUrl))) {
        redirectUrl = decodeURIComponent(returnUrl);
      } else {
        redirectUrl = getSafeRedirectUrl();
      }
      navigate(redirectUrl, { replace: true });
    }
  }, [navigate, handleCompleteAlexaOAuth]);

  // Handle Alexa OAuth parameters detection
  useEffect(() => {
    const isAlexaLinking = searchParams.get('alexa_linking') === 'true';
    const authCode = searchParams.get('auth_code');

    if (isAlexaLinking) {
      if (!authCode) {
        setError('Missing authorization code for Alexa account linking.');
        return;
      }

      // Store OAuth params for potential use
      setAlexaOAuthParams({ authCode, clientId: '' });

      // REMOVED auto-completion as it was interfering with mobile OAuth flow
      // Always require fresh login for Alexa OAuth
      // The OAuth completion will happen after successful login in handleSubmit
    }
  }, [searchParams, handleCompleteAlexaOAuth]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      let response;
      if (isLogin) {
        response = await login(formData.email, formData.password);
      } else {
        response = await register(formData.email, formData.password, formData.firstName, formData.lastName);
        
        // Record policy acknowledgments after successful registration
        if (response && policies.privacy && policies.terms) {
          try {
            await Promise.all([
              acknowledgePolicyDocument({
                documentType: 'PrivacyPolicy',
                documentVersion: policies.privacy.version
              }),
              acknowledgePolicyDocument({
                documentType: 'TermsOfService', 
                documentVersion: policies.terms.version
              })
            ]);
          } catch (policyError) {
            console.error('Failed to record policy acknowledgments:', policyError);
            // Don't fail registration if policy acknowledgment fails, just log it
          }
        }
      }

      // Check if we need to complete Alexa OAuth flow after authentication
      if (alexaOAuthParams) {
        await handleCompleteAlexaOAuth({ authCode: alexaOAuthParams.authCode });
        return; // OAuth completion will handle redirect
      }
      
      // Route based on nextAction from backend
      if (response.nextAction) {
        switch (response.nextAction) {
          case 'family_setup':
            navigate(`${basePath}-setup`, { replace: true });
            break;
          case 'dashboard':
            navigate('/', { replace: true });
            break;
          case 'invite_acceptance':
            // Future feature - issue #190
            navigate('/invites', { replace: true });
            break;
          case 'account_status':
            // Future feature - issue #191
            navigate('/account-status', { replace: true });
            break;
          default: {
            // Fallback to redirect URL or dashboard
            const redirectUrl = getSafeRedirectUrl();
            navigate(redirectUrl, { replace: true });
          }
        }
      } else {
        // Fallback if no nextAction provided (backwards compatibility)
        const redirectUrl = getSafeRedirectUrl();
        navigate(redirectUrl, { replace: true });
      }
    } catch (err: unknown) {
      setError((axios.isAxiosError(err) ? err.response?.data?.message : undefined) || 'Authentication failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Validation helper functions
  const getPasswordValidationError = (): string | null => {
    if (!touched.password || formData.password.length === 0) return null;
    return validatePassword(formData.password);
  };

  const getConfirmPasswordError = (): string | null => {
    if (!touched.confirmPassword || formData.confirmPassword.length === 0) return null;
    return validateConfirmPassword(formData.password, formData.confirmPassword);
  };

  const isFormValid = (): boolean => {
    if (isLogin) {
      return formData.email.length > 0 && formData.password.length > 0;
    } else {
      const baseFormValid = isRegistrationFormValid(
        formData.email,
        formData.password,
        formData.confirmPassword,
        formData.firstName,
        formData.lastName
      );
      
      // For registration, also require policy acceptance
      const policiesAccepted = policyAccepted.privacy && policyAccepted.terms;
      return baseFormValid && policiesAccepted;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  const handleBlur = (field: 'password' | 'confirmPassword') => () => {
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));
  };

  const togglePasswordVisibility = (field: 'password' | 'confirmPassword') => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const checkIfPolicyScrollable = (element: HTMLDivElement | null, policyType: 'privacy' | 'terms') => {
    if (!element) return;

    const { scrollHeight, clientHeight } = element;
    const needsScroll = scrollHeight > clientHeight;

    // If content doesn't need scrolling, automatically mark as scrolled
    if (!needsScroll && !policyScrolled[policyType]) {
      setPolicyScrolled(prev => ({
        ...prev,
        [policyType]: true
      }));
    }
  };

  const handlePolicyScroll = (policyType: 'privacy' | 'terms') => (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const scrolled = target.scrollTop + target.clientHeight >= target.scrollHeight - 10;

    if (scrolled && !policyScrolled[policyType]) {
      setPolicyScrolled(prev => ({
        ...prev,
        [policyType]: true
      }));
    }
  };

  const handlePolicyAcceptanceChange = (policyType: 'privacy' | 'terms') => (e: React.ChangeEvent<HTMLInputElement>) => {
    setPolicyAccepted(prev => ({
      ...prev,
      [policyType]: e.target.checked
    }));
  };

  // Helper function to reset form state when switching between login/register modes
  const resetFormState = () => {
    setError('');
    setFormData({ email: '', password: '', confirmPassword: '', firstName: '', lastName: '' });
    setShowPasswords({ password: false, confirmPassword: false });
    setTouched({ password: false, confirmPassword: false });
  };

  const renderMarkdown = renderPolicyMarkdown;

  return (
    <Container 
      maxWidth="sm" 
      sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        py: 3,
        background: theme => theme.palette.gradient.secondary,
      }}
    >
      <Paper elevation={3} sx={{ width: '100%', p: 4 }}>
        {/* Header */}
        <Box textAlign="center" mb={4}>
          <Avatar
            sx={{
              width: 108,
              height: 108,
              mx: 'auto',
              mb: 2
            }}
          >
            <Box
              component="img"
              src="/lets-reset-logo-108.png"
              alt="Let's Reset logo"
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Avatar>
          <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom>
            Let&apos;s Reset
          </Typography>
          <Typography variant="h5" component="h2" color="text.primary" gutterBottom>
            Welcome to Your Family&apos;s Journey
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Parenting isn&apos;t about being perfect — it&apos;s about learning to make things right, together. <strong>Let&apos;s Reset</strong> helps your family track behaviors hands-free, celebrate progress, and restore balance one choice at a time.
          </Typography>
          <List sx={{ 
            maxWidth: 400, 
            mx: 'auto', 
            mb: 3,
            '& .MuiListItem-root': {
              py: 0.5
            }
          }}>
            {FEATURE_HIGHLIGHTS.map((feature, index) => (
              <ListItem key={index}>
                <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
                  <Typography variant="body1" component="span">{feature.emoji}</Typography>
                </ListItemIcon>
                <ListItemText primary={feature.text} />
              </ListItem>
            ))}
          </List>
          {alexaOAuthParams && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Alexa Account Linking</strong><br />
                Please sign in to link your Let&apos;s Reset account with Alexa.
              </Typography>
            </Alert>
          )}

          {/* Primary CTA - Create New Account */}
          <Box sx={{ mt: 4, mb: 3, textAlign: 'center' }}>
            <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 600 }}>
              Ready to Get Started?
            </Typography>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={() => {
                setIsLogin(false);
                resetFormState();
                loadPolicies();
              }}
              sx={{
                py: 2,
                fontSize: '1.2rem',
                fontWeight: 600,
                borderRadius: 2,
                textTransform: 'none',
                background: theme => theme.palette.gradient.primary,
                '&:hover': {
                  background: theme => theme.palette.primary.dark,
                  transform: 'translateY(-1px)',
                  boxShadow: theme => theme.shadows[8]
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              Create New Account
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Take the first step toward better family moments
            </Typography>
          </Box>

        </Box>

        {/* Visual separation between intro and form - only show if social providers available */}
        {hasSocialProviders && <Divider sx={{ mb: 3 }} />}

        {/* Social Login Buttons */}
        <SocialLoginButtons
          isLoading={isLoading}
          onAvailabilityChange={setHasSocialProviders}
        />

        {/* Divider - conditionally show based on provider availability */}
        {hasSocialProviders !== null && (
          <Box my={3}>
            <Divider>
              <Typography variant="body2" color="text.secondary">
                {hasSocialProviders ? 'Or continue with email' : 'Login with Email'}
              </Typography>
            </Divider>
          </Box>
        )}

        {/* Alexa OAuth Completion */}
        {isCompletingAlexaOAuth && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography variant="body1">
              Completing Alexa account linking...
            </Typography>
          </Box>
        )}

        {/* Alexa OAuth Notice */}
        {alexaOAuthParams && !isCompletingAlexaOAuth && (
          <Alert severity="info" sx={{ mb: 3 }}>
            You will be redirected to complete Alexa account linking after {isLogin ? 'login' : 'registration'}.
          </Alert>
        )}

        {/* Form */}
        <Box component="form" onSubmit={handleSubmit}>
          {!isLogin && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required={!isLogin}
                  variant="outlined"
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required={!isLogin}
                  variant="outlined"
                />
              </Grid>
            </Grid>
          )}

          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            variant="outlined"
            margin="normal"
          />

          <TextField
            fullWidth
            label="Password"
            name="password"
            type={showPasswords.password ? 'text' : 'password'}
            value={formData.password}
            onChange={handleInputChange}
            onBlur={!isLogin ? handleBlur('password') : undefined}
            required
            variant="outlined"
            margin="normal"
            error={!isLogin && !!getPasswordValidationError()}
            helperText={!isLogin ? (getPasswordValidationError() || "Must be between 8 and 64 characters") : undefined}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => togglePasswordVisibility('password')}
                    edge="end"
                    aria-label="toggle password visibility"
                  >
                    {showPasswords.password ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {!isLogin && (
            <TextField
              fullWidth
              label="Confirm Password"
              name="confirmPassword"
              type={showPasswords.confirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleInputChange}
              onBlur={handleBlur('confirmPassword')}
              required
              variant="outlined"
              margin="normal"
              error={!!getConfirmPasswordError()}
              helperText={getConfirmPasswordError() || "Re-enter the password to confirm"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => togglePasswordVisibility('confirmPassword')}
                      edge="end"
                      aria-label="toggle password visibility"
                    >
                      {showPasswords.confirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}


          {/* Policy Documents for Registration */}
          {!isLogin && (
            <Box sx={{ mt: 3, mb: 2 }}>
              <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
                Please review and accept our policies
              </Typography>

              {/* Button to open larger modal view */}
              <Button
                variant="outlined"
                onClick={() => setShowPolicyModal(true)}
                sx={{ mb: 2 }}
                fullWidth
              >
                Open Policies in Larger View
              </Button>

              {loadingPolicies ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {/* Privacy Policy */}
                  {policies.privacy && (
                    <Grid size={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                            Privacy Policy
                          </Typography>
                          {/* Renders raw markdown HTML via dangerouslySetInnerHTML; no Typography variant applies */}
                          <Box
                            ref={(el) => checkIfPolicyScrollable(el as HTMLDivElement | null, 'privacy')}
                            sx={{
                              height: 200,
                              overflow: 'auto',
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              p: 2,
                              mb: 2,
                              fontSize: '0.875rem',
                              lineHeight: 1.5
                            }}
                            onScroll={handlePolicyScroll('privacy')}
                            dangerouslySetInnerHTML={{
                              __html: renderMarkdown(policies.privacy.content)
                            }}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={policyAccepted.privacy}
                                onChange={handlePolicyAcceptanceChange('privacy')}
                                disabled={!policyScrolled.privacy}
                              />
                            }
                            label="I accept the Privacy Policy"
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                  {/* Terms of Service */}
                  {policies.terms && (
                    <Grid size={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                            Terms of Service
                          </Typography>
                          {/* Renders raw markdown HTML via dangerouslySetInnerHTML; no Typography variant applies */}
                          <Box
                            ref={(el) => checkIfPolicyScrollable(el as HTMLDivElement | null, 'terms')}
                            sx={{
                              height: 200,
                              overflow: 'auto',
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              p: 2,
                              mb: 2,
                              fontSize: '0.875rem',
                              lineHeight: 1.5
                            }}
                            onScroll={handlePolicyScroll('terms')}
                            dangerouslySetInnerHTML={{
                              __html: renderMarkdown(policies.terms.content)
                            }}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={policyAccepted.terms}
                                onChange={handlePolicyAcceptanceChange('terms')}
                                disabled={!policyScrolled.terms}
                              />
                            }
                            label="I accept the Terms of Service"
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              )}
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
              {alexaOAuthParams && error.includes('authorization code') && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate('/integrations')}
                  >
                    Go to Integrations
                  </Button>
                </Box>
              )}
            </Alert>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={!isFormValid() || isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : <LoginIcon />}
            sx={{ mt: 3 }}
          >
            {isLoading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Register')}
          </Button>
        </Box>

        {/* Forgot Password Link for Login */}
        {isLogin && (
          <Box textAlign="center" mt={2}>
            <Link
              component="button"
              variant="body2"
              onClick={() => navigate('/forgot-password')}
              sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              Forgot Password?
            </Link>
          </Box>
        )}

        {/* Show switch back to login option when in registration mode */}
        {!isLogin && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button
              variant="text"
              onClick={() => {
                setIsLogin(true);
                resetFormState();
              }}
              sx={{ textDecoration: 'underline' }}
            >
              Already have an account? Sign In
            </Button>
          </Box>
        )}

      </Paper>

      {/* Footer with Policy Links */}
      <Footer variant="login" />

      {/* Policy Acceptance Modal for Registration */}
      {!isLogin && showPolicyModal && (
        <PolicyAcceptanceModal
          open={showPolicyModal}
          onAccepted={() => {
            // Mark both policies as accepted when modal is accepted
            setPolicyAccepted({ privacy: true, terms: true });
            setPolicyScrolled({ privacy: true, terms: true });
            setShowPolicyModal(false);
          }}
          onClose={() => setShowPolicyModal(false)}
          skipApiCall={true}
        />
      )}
    </Container>
  );
};

export default Login;